/**
 * @fileoverview Cortex Transcription Service - Phase 4 Compliant
 * @module services/cortex/transcription
 * @description Handles real-time transcription processing and events from ElevenLabs.
 *
 * @implements {ICortexTranscription}
 * @implements {IService}
 * @service-registry
 * **Registry Configuration:**
 * - Key: 'cortexTranscription'
 * - Factory: `() => cortexTranscription`
 * - Dependencies: []
 * - Lifecycle: singleton
 *
 * @architecture Phase 4 compliant service
 * @version 1.0.0 - Phase 4 implementation
 * @since 1.0.0 - Modular architecture implementation
 */
import type { ICortexTranscription, TranscriptionEvent, TranscriptionState, } from './types';
import type { IService, ServiceResponse } from '../service-registry';
import { logger } from "../../lib/logger";
/**
 * Cortex Transcription Service
 * Processes and manages transcription events from voice conversations
 */
class CortexTranscriptionService implements ICortexTranscription {
    readonly name = 'cortexTranscription' as const;
    private transcriptionState: TranscriptionState = {
        isUserStreaming: false,
        isAgentStreaming: false,
        lastUpdate: new Date(),
    };
    private transcriptionHandlers = new Set<(event: TranscriptionEvent) => void>();
    /**
     * Initialize the service
     */
    async initialize(): Promise<void> {
        logger.info('🚀 Initializing Cortex Transcription Service...');
        // Service is ready
    }
    /**
     * Dispose the service
     */
    async dispose(): Promise<void> {
        logger.info('🧹 Disposing Cortex Transcription Service...');
        this.transcriptionHandlers.clear();
        this.transcriptionState = {
            isUserStreaming: false,
            isAgentStreaming: false,
            lastUpdate: new Date(),
        };
    }
    /**
     * Process a transcription event from ElevenLabs
     */
    async processTranscription(event: TranscriptionEvent): Promise<ServiceResponse<void>> {
        try {
            // Update transcription state
            if (event.role === 'user') {
                if (event.type === 'transcription_start') {
                    this.transcriptionState.isUserStreaming = true;
                    this.transcriptionState.currentUserTranscription = '';
                }
                else if (event.type === 'transcription_update') {
                    this.transcriptionState.currentUserTranscription = event.text;
                }
                else if (event.type === 'transcription_complete') {
                    this.transcriptionState.isUserStreaming = false;
                    this.transcriptionState.currentUserTranscription = event.text;
                }
            }
            else if (event.role === 'agent') {
                if (event.type === 'transcription_start') {
                    this.transcriptionState.isAgentStreaming = true;
                    this.transcriptionState.currentAgentTranscription = '';
                }
                else if (event.type === 'transcription_update') {
                    this.transcriptionState.currentAgentTranscription = event.text;
                }
                else if (event.type === 'transcription_complete') {
                    this.transcriptionState.isAgentStreaming = false;
                    this.transcriptionState.currentAgentTranscription = event.text;
                }
            }
            this.transcriptionState.lastUpdate = new Date();
            // Emit to all handlers
            this.transcriptionHandlers.forEach((handler) => {
                try {
                    handler(event);
                }
                catch (error) {
                    logger.error('Error in transcription handler:', error);
                }
            });
            return { success: true, data: undefined };
        }
        catch (error) {
            const errorMessage = error instanceof Error
                ? error.message
                : 'Failed to process transcription';
            return { success: false, error: errorMessage };
        }
    }
    /**
     * Get current transcription state
     */
    async getTranscriptionState(): Promise<ServiceResponse<TranscriptionState>> {
        return { success: true, data: { ...this.transcriptionState } };
    }
    /**
     * Subscribe to transcription events
     */
    onTranscription(handler: (event: TranscriptionEvent) => void): void {
        this.transcriptionHandlers.add(handler);
    }
    /**
     * Unsubscribe from transcription events
     */
    offTranscription(handler: (event: TranscriptionEvent) => void): void {
        this.transcriptionHandlers.delete(handler);
    }
}
// Export singleton instance
export const cortexTranscription = new CortexTranscriptionService();
